// i18n area 'vms' (task #127): sections vms.
// Split out of the old monolithic dictionaries.ts by scripts/split-dictionaries.mjs;
// since the split, these files are the SOURCE — add and edit keys here. The script
// only re-runs to re-split a merged monolithic dictionaries.ts (see its header).

import type { PluralForms } from '../plural'

// Helper locale al file, identico a plural() in ../plural: un import RUNTIME
// da una sezione romperebbe scripts/i18n-status.mjs, che carica questi file
// con node (type stripping, niente resolver TS) — l'import type si cancella,
// uno runtime no. Il tipo resta l'unico e vero contratto.
const plural = (forms: PluralForms): PluralForms => forms

export const enUS = {
  vms: {
    spof: {
      title: 'Node independence',
      single: 'Single point of failure',
      alternativesNow: 'Alternatives now',
      structural: 'By construction',
      binding: 'Binding constraint',
      movableNow: 'Movable now',
    },
    title: 'Virtual machines',
    migratingChip: 'Migrating',
    templateChip: 'Template',
    expendableTooltip: 'In a degraded cluster it is among the first to be stopped and it may not restart after a node loss.',
    expendableHaTooltip: 'Always restarted while the cluster is healthy; in a degraded cluster protected VMs come first — it can be stopped or stay off until the cluster is whole again, then it returns on its own.',
    viewDetails: 'View details',
    profileLegacy: 'Other OS',
    hugePagesNone: 'None — ordinary memory',
    hugePages2M: '2 MiB pages',
    hugePages1G: '1 GiB pages',
    searchPlaceholder: 'Search by name',
    name: 'Name',

    // Terminologia UNA (#94a): la phase si chiama 'Phase' ovunque — la
    // vecchia chiave 'state' è morta col polish della lista.
    vcpus: 'vCPU',
    memory: 'Memory',
    ipAddresses: 'IP addresses',
    noIp: 'No IP',
    authorityNode: 'Authority',
    authorityNextStart: 'assigned at the next start',
    scope: 'Scope',
    empty: 'No virtual machines',
    loadError: 'Could not load the virtual machines — is the TVirt backend reachable?',
    filterOverlay: 'overlay network',
    filterDisk: 'disk',
    filterImage: 'image',
    filteredBy: 'Filtered by {kind}',
    filterPhase: 'Phase',
    filterProtection: 'Protection',
    filterAll: 'All',
    filterScopePlaceholder: 'Filter by scope',
    filtersToggle: 'Filters',
    scopeTree: {
      title: 'Scopes',
      all: 'Whole cluster',
      noChildren: 'No sub-scopes here',
      recursive: 'Include descendants',
      collapse: 'Collapse the scope panel',
      up: 'Up one level',
      countTooltip: '{direct} VMs in this scope · {total} including descendants'
    },

    // The two-step ISO picker (#85): catalog first, then the ISOs it holds.
    isoPicker: {
      catalog: 'Catalog',
      unknownCatalog: 'Not in a visible catalog',
      noIsos: 'No ready ISO images in this catalog'
    },
    actionsHeader: 'Actions',
    templatesTab: {
      quickDeploy: 'QuickDeploy',
      hardware: 'Hardware',
      tags: 'Tags',
      age: 'Age',
      disk: 'disk',
      disks: 'disks',
      empty: 'No templates yet — mark a stopped VM as a template, or clone one as a template.',
      clone: 'Clone / deploy',
      editTags: 'Edit tags',

      // Round K3 (#64): export the template as a .tvtpl catalog file.
      exportAction: 'Export to catalog (.tvtpl)',
      exportTitle: 'Export to catalog',
      exportSubmit: 'Export',
      exportError: 'Export failed',
      exportHint: 'The template is packed into a .tvtpl archive (hardware manifest + every disk flattened to qcow2) as a new VM Template file in the chosen catalog. NICs, CD/DVD media and cloud-init do not travel — the format spec lists every deliberate omission.',
      exportName: 'File name',
      exportNameHint: 'Blank = <template>-export-<n>',
      exportStorageDefault: 'The template’s metadata storage',

      // Import from catalog (Luca 2026-08-03): the toolbar entry of the
      // Templates tab — the K3/K4 backend covers every vm-template format,
      // the file IS the choice (its format rides along read-only).
      importAction: 'Import from catalog',
      importTitle: 'Import from catalog',
      importHint: 'A ready VM Template file of the chosen catalog becomes a template of the inventory: the file’s format (tvtpl, ova, ovf or naked qcow2) decides how it imports — you only pick the file.',
      importFile: 'Catalog file',
      importFileHint: 'Only ready files import — one mid-upload is not importable',
      importNoFiles: 'No ready VM Template files in this catalog — upload or export one first'
    },
    templateDetail: {
      quickDeploy: 'QuickDeploy compatibility',
      quickDeployNone: 'Not declared',
      quickDeployHint: 'Declare it only if the golden image carries cloud-init (Linux) or cloudbase-init after sysprep /generalize (Windows): the platform trusts the declaration and never looks inside the guest.',
      notFound: 'Template not found',
      frozen: 'The hardware configuration is frozen: convert the template back to a VM to change it. Name, notes and tags stay editable.',
      storage: 'Storage',
      size: 'Size',

      // Round #156 (dialog standard): the frozen config sections that were
      // invisible until the template was unmarked.
      configTitle: 'Configuration (frozen)',
      usbClasses: 'USB classes required',
      gpuClasses: 'GPU classes required'
    },

    // Round #173 (T173-P8): the shared-disks tab, the DECLARED-property sister
    // of unusedDisksTab, plus the sharing/LUN fields of the disk dialogs.
    sharedDisksTab: {
      title: 'Shared disks',
      sharing: 'Sharing',
      sharingNone: 'Single attach',
      sharingMultiwriter: 'Shared write (multiwriter)',
      sharingReadonly: 'Shared read-only',
      usedBy: 'In use by',
      attachedTo: 'Attached to',
      notAttached: 'Attached to no VM',
      changeSharing: 'Change sharing mode',
      changeSharingTitle: 'Sharing mode',
      changeSharingLocked: 'The mode changes only while no VM attaches the disk',
      sharingChanged: 'Sharing mode updated.',
      lun: 'Whole LUN',
      lunNumber: 'LUN / namespace',
      lunWwn: 'WWN',
      lunSerial: 'Serial',
      antiAffinityWarning: 'Two or more members run on the same node without an anti-affinity rule: one node failure takes them all down',
      antiAffinityCreate: 'Create the rule',
      createSourceLun: 'Adopt a whole LUN of the target',
      createLunNumber: 'LUN number (namespace id on NVMe)',
      createLunHint: 'Nothing is allocated: the size is observed from the device at adoption',
      noPr: 'No SCSI-3 persistent reservations in this release: a Windows Failover Cluster with a classic shared disk is not validated; clusters that coordinate over the network (GFS2/OCFS2, RHEL HA, shared-disk databases) are'
    },
    unusedDisksTab: {
      title: 'Unused disks',
      attach: 'Attach to VM',
      attachTitle: 'Attach to a VM',
      vm: 'Virtual machine',
      noVms: 'No VMs available',
      noSlots: 'No free slots on this VM',

      // Standalone disk creation (#87): back from the retired Inventory
      // section, now with an EXPLICIT scope field (no VM to inherit from).
      create: 'Create disk',
      createTitle: 'Create disk',

      // #263: seeding the new disk from a catalog image — the size prefills
      // from the image's VIRTUAL size (never the file size) and only grows.
      createSourceBlank: 'Empty disk',
      createSourceImage: 'From a catalog image',
      createImage: 'Image',
      createImageEmpty: 'No ready VM template image in the visible catalogs',
      createSizeFromImageHint: 'Prefilled with the image\'s virtual size — it can only grow',

      // #107 (DESTRUCTIVE, ratified): qcow2 → vm-template catalog file on
      // the same storage, zero bytes copied — the disk ceases to exist.
      convert: 'Convert into a VM template…',
      convertOnlyQcow2: 'Only qcow2 disks convert — raw is not a registrable template format',
      convertTitle: 'Convert into a VM template',
      convertSubmit: 'Convert (destroys the disk)',
      convertError: 'The conversion could not start',
      convertWarning: 'DESTRUCTIVE: the disk becomes a VM template file in the chosen catalog — same storage, no bytes copied — and CEASES TO EXIST as a disk. There is no way back except creating a new disk from the template.',
      convertNameHint: 'The catalog file\'s name — defaults to the disk\'s own',
      convertConfirm: 'I understand: the disk will be destroyed',
      convertStarted: 'Conversion started — the template appears in the catalog when the task completes.',

      // Round #156 (dialog standard): the observed half of the disk in the
      // details dialog, plus the lock hint for the action-only storage move.
      storageLockHint: 'The storage changes only through the Move action',
      source: 'Provenance',
      sourceClone: 'Clone of another disk',
      sourceCloneLinked: 'Linked clone of another disk',
      sourceImage: 'Created from an image',
      sourceBlank: 'Created empty',
      allocated: 'Allocated / virtual size',
      observedAt: 'Observed at',
      snapshots: 'Snapshots',
      snapshotsEmpty: 'No snapshots'
    },
    phase: {
      defined: 'Never powered on',
      starting: 'Starting',
      running: 'Running',
      stopping: 'Stopping',
      stopped: 'Stopped',
      paused: 'Paused',
      failed: 'Failed',
      deleting: 'Deleting'
    },
    stopCause: {
      'api': 'Stopped by TVirt (action or API)',
      'guest-shutdown': 'Shutdown initiated by the guest OS',
      'crash': 'Guest crash',
      'qemu-exit': 'The QEMU process exited unexpectedly',
      'host-reboot': 'The node rebooted',
      'migrated': 'Migrated to another node',
      'fenced': 'Terminated by HA fencing (returning node)'
    },
    detail: {
      notFound: 'Virtual machine not found',
      details: 'Details',
      id: 'ID',
      scope: 'Scope',
      profile: 'Hardware profile',
      firmware: 'Firmware',
      firmwareFile: 'UEFI firmware version',
      firmwareUpdate: 'Update available',
      firmwareUpdateHint: 'The platform ships a newer UEFI firmware. The update is never automatic: it applies with the VM stopped, when you decide.',
      arch: 'Architecture',
      node: 'Node',
      bootOrder: 'Boot order',
      agent: 'Agent status',
      agentConnected: 'Connected',
      agentNotConnected: 'Not connected',
      protected: 'Protected',
      linkedClones: 'Linked clones',
      restartPolicy: 'Restart policy',
      preferredNode: 'Preferred node',
      noPreference: 'No preference (automatic placement)',
      preferNodes: 'Preferred node tags',
      requireNodes: 'Required node tags',
      noPlacementTags: 'None',
      placementTag: 'Tag',
      placementValue: 'Value',
      placementAddTag: 'Add tag',
      proximityHa: 'TVirt Proximity HA',
      proximityHaRestarts: 'HA restarts',
      proximityHaGaveUp: 'crash loop — HA gave up',
      failoverProtection: 'Protection in degraded cluster',
      protectionProtected: 'Protected',
      protectionExpendable: 'Expendable when degraded',
      protectionInherit: 'Inherit',
      lastStop: 'Last stop',
      yes: 'Yes',
      no: 'No',
      tabOverview: 'Overview',
      tabSnapshots: 'Snapshots',
      tabBackups: 'Backups',
      hardware: 'Hardware',
      name: 'Name',
      hwSystem: 'System',
      hwCpu: 'CPU',
      hwMemory: 'Memory',
      hwBoot: 'Boot',
      hwIdentity: 'Identity',
      hwCluster: 'Cluster & agent',
      cpuTopology: 'CPU topology',
      cpuModel: 'CPU model',
      cpuLimit: 'CPU limit',
      cpuWeight: 'CPU weight',
      cpuNested: "Nested virtualization",
      cpuNestedHint: "Exposes vmx/svm to the guest (host CPU model only). On a running VM the change applies at the next power cycle (PendingRestart); placement only admits nodes with nested enabled.",
      unlimited: 'Unlimited',
      memory: 'Memory',
      balloon: 'Balloon',
      hugePages: 'Huge pages',
      hugePagesTradeoff: 'Less address-translation work and memory that never goes to disk; in exchange the reservation stays locked on the node while the VM runs, memory is no longer deduplicated, and the start fails if the node has too few free pages.',
      disks: 'Disks',
      cdroms: 'CD/DVD drives',
      nics: 'Network interfaces',
      slot: 'Slot',
      disk: 'Disk',
      encrypted: 'Encrypted',
      image: 'Image',
      emptyDrive: 'Empty drive',
      emptyOption: '(empty)',
      network: 'Network',
      mac: 'MAC',
      connected: 'Connected',
      unplugged: 'Unplugged',
      noDevices: 'None',
      screenshot: 'Screenshot',
      capturedAt: 'Captured at',
      noCapture: 'No capture yet',
      snapshotName: 'Name',
      snapshotCreated: 'Created',
      snapshotMemory: 'Memory included',
      noSnapshots: 'No snapshots',
      pendingRestart: 'Changes pending: they will apply at the next power cycle.',
      applied: 'applied',
      appliedHint: 'The running VM booted with this value — the configured one applies at the next power cycle.',
      actionError: 'Action failed',
      actionStillRunning: 'The action is still running — its outcome is not yet known',
      tabConsole: 'Console',
      consoleConnect: 'Connect',
      consoleDisconnect: 'Disconnect',
      consoleConnecting: 'Connecting…',
      consoleReconnecting: 'Connecting…',
      consoleUnavailable: 'The console is available while the VM is running or paused.',
      consoleError: 'Console connection failed',
      consoleTaken: 'Console session taken over by {user}',
      consoleReconnect: 'Reconnect',
      consoleCtrlAltDel: 'Ctrl+Alt+Del',
      consoleHold: 'Hold {key} down',
      consoleFn: 'Fn…',
      consoleFullscreen: 'Open full screen in a new tab',
      consoleLoadFailed: 'VM not found or insufficient permissions',
      consoleClose: 'Close',
      tabStats: 'Statistics',
      tabTasks: 'Tasks',
      tabLogs: 'Logs',
      statsCpu: 'CPU',
      statsMemory: 'Memory',
      statsDisk: 'Disk I/O',
      statsRange: 'Range',
      statsNoData: 'No data for the selected range — statistics are sampled while the VM runs.',
      statsError: 'Could not load the statistics',
      cpuUsage: 'Usage',
      cpuReady: 'Ready (%RDY)',
      memUsed: 'Used',
      memGuestAvailable: 'Guest available',
      schema: 'Schema',
      consoleProtocol: 'Console',
      agentEnabled: 'Guest agent',
      metadataStorage: 'Metadata storage',
      startupOnBoot: 'Start on boot',
      startupOrder: 'Startup order',
      startupDelay: 'Startup delay',
      seconds: 's',
      enabled: 'Enabled',
      disabled: 'Disabled'
    },
    actionsAvailable: 'Available actions',
    actions: {
      start: 'Start',
      stop: 'Stop',
      pause: 'Pause',
      resume: 'Resume',
      reset: 'Reset',
      migrate: 'Migrate',
      clone: 'Clone',
      moveStorage: 'Move to storage…',
      moveScope: 'Move to scope…',
      openConsole: 'Open console',
      forceStop: 'Force stop',
      markTemplate: 'Mark as template',
      unmarkTemplate: 'Convert to VM',
      error: 'Could not run the action'
    },

    // #373 — la proposta del cordone di manutenzione sull'accensione
    // d'emergenza. Il corpo NON sta qui: è il detail del backend, che nomina
    // il nodo.
    cordon: {
      confirmTitle: 'The node is in maintenance',
      confirmHint:
        'The cordon cannot be lowered while the cluster store is down. Confirming declares that you know: the VM powers on there anyway, and the consent is recorded on the task.',
      confirm: 'Power on anyway'
    },
    markTemplate: {
      confirmTitle: 'Mark {name} as a template?',
      confirmBody: 'The configuration freezes and the VM cannot be powered on anymore — only cloned, or converted back. Name, notes and tags stay editable.',
      confirm: 'Mark as template',
      moved: '{name} is now a template — it moved to the Templates tab.'
    },
    unmarkTemplate: {
      confirmTitle: 'Convert {name} back to a VM?',
      confirmBody: 'The template becomes an ordinary VM again: editable and startable (the next start places it on a node).',
      confirm: 'Convert to VM',
      moved: '{name} is a virtual machine again — it moved to the Virtual machines tab.'
    },

    // Round #123: riferimento a nodo per TAG (selettore #106) nei dialog
    // di start e migrazione — anteprima dell'insieme risolto inclusa.
    nodeByTag: {
      label: 'By node tag',
      hint: 'tag=value, comma-separated (ANDed): the decider picks among the matching nodes',
      invalid: 'Invalid format — use tag=value, comma-separated',
      resolved: 'Matching members: {nodes}'
    },
    migrate: {
      title: 'Live migration',
      submit: 'Migrate',
      target: 'Target node',
      auto: 'Automatic (Proximity)',
      hint: 'The VM moves without a power cycle. Every storage it touches must be shared; a paused VM migrates paused.',
      error: 'The migration was refused'
    },
    moveStorage: {
      title: 'Move to storage',
      submit: 'Move',
      target: 'Target storage',
      noTargets: 'No storage accepts disks',
      hint: 'Every disk of the stopped VM travels to the chosen storage, one at a time, keeping its id — disks already there are skipped. The task page follows the progress.',
      error: 'The storage move was refused'
    },
    moveScope: {
      title: 'Move to scope',
      submit: 'Move',
      target: 'Destination scope',
      hint: 'The VM changes scope immediately — id and disks stay, its owned disks follow, and it can stay powered on. It currently lives in {scope}. Who sees it changes with the scope.',
      error: 'The scope move was refused'
    },
    start: {
      title: 'Start VM',
      submit: 'Start',
      target: 'Destination node',
      auto: 'Automatic (recommended)',
      recommended: 'recommended',
      hint: 'Pick a destination or let Proximity place the VM among the valid candidates. Nodes that cannot host it are disabled with the reason.',
      error: 'The VM could not be started'
    },
    console: {
      media: 'CD/DVD drives',
      mediaGuestToolsHint: 'Holds the TVirt Guest Tools — eject or remove it right here',
      slotOpsDeferred: 'On this profile adding/removing drives applies at the next power cycle (media changes are immediate).',
      mountGuestTools: 'Mount TVirt Guest Tools',
      ejectGuestTools: 'Eject TVirt Guest Tools',
      bootOrder: 'Boot order',
      bootOrderHint: 'The new order saves immediately and applies at the next power cycle — the console session stays connected.'
    },
    snapshots: {
      create: 'Take snapshot',
      createTitle: 'Take a snapshot',
      createSubmit: 'Snapshot',
      name: 'Name',
      nameHint: 'Letters, digits, dot, dash, underscore.',
      consistencyHint: 'Consistency is automatic: the guest agent freezes the filesystems when it responds; otherwise the VM pauses briefly around the snapshot.',
      memory: 'Include memory',
      memoryHint: 'Captures the live RAM: reverting resumes the VM running. Requires every disk qcow2.',
      currentChip: 'You are here',
      revert: 'Revert',
      revertTitle: 'Revert to {name}?',
      revertBody: 'Disks return to the snapshotted state; everything after it is lost. A disk-only snapshot needs the VM stopped.',
      delete: 'Delete',
      deleteTitle: 'Delete snapshot {name}?',
      deleteBody: 'The restore point disappears; disk contents are untouched.',
      deleteMergeHint: 'Its delta merges into {children}: every later restore point stays valid.',
      deleteDuplication: 'I accept the duplication',
      deleteDuplicationHint: 'The delta is copied into each of the {count} readers (the child snapshots and, if this is the current one, the active head): the used space can grow instead of shrinking.',
      error: 'The snapshot operation was refused'
    },
    clone: {
      title: 'Clone VM',
      submit: 'Clone',
      name: 'Name of the copy',
      storage: 'Target storage for the disks',
      sameStorage: 'Same as the source',
      full: 'Full copy',
      fullHint: 'Copies every byte and severs the tie with the source disks. Default is a linked clone: instant, but chained to the source (qcow2 only).',
      hint: 'The clone keeps the source configuration and firmware; MAC addresses and TPM identity are fresh.',
      markAs: 'Clone kind',
      markAsInheritVm: 'Same as the source (VM)',
      markAsInheritTemplate: 'Same as the source (template)',
      markAsVm: 'Virtual machine',
      markAsTemplate: 'Template',
      markAsHintVm: 'Choose Template to promote the copy in one step.',
      markAsHintTemplate: 'Choose Virtual machine to deploy from this template in one step.',
      error: 'The clone was refused'
    },
    bulk: {
      selected: 'selected',
      deleteTitle: plural({
        one: 'Delete {count} selected VM?',
        other: 'Delete {count} selected VMs?'
      }),
      deleteBody: 'Each VM passes the same guards as a single delete; refusals are reported per item.',
      done: 'accepted',
      refused: 'refused'
    },
    delete: {
      button: 'Delete',
      confirmTitle: 'Delete {name}?',
      confirmBody: 'The virtual machine and the disks it owns will be deleted. This cannot be undone.',
      keepDisks: 'Keep the disks',
      keepDisksHint: 'The disks created with the VM stay in their storage as standalone disks.',
      confirm: 'Delete',
      cancel: 'Cancel',
      error: 'Deletion failed',
      blockedPhase: 'Stop the VM first',
      blockedProtected: 'The VM is protected — clear the protection first',
      blockedDeleting: 'Deletion already in progress'
    },
    logs: {
      title: 'Logs',
      subtitle: 'Structured log frames from the tasks targeting this VM',
      when: 'When',
      task: 'Task',
      level: 'Level',
      message: 'Message',
      empty: 'No log entries yet',
      loadError: 'Could not load the logs — is the TVirt backend reachable?'
    },
    stop: {
      title: 'Stop virtual machine',
      modeGraceful: 'Graceful shutdown',
      modeGracefulHint: 'The guest OS is asked to shut down cleanly (ACPI); if it ignores the request, a forced stop follows after the timeout.',
      modeForced: 'Forced stop',
      modeForcedHint: 'Immediate power cut — like pulling the plug.',
      timeout: 'Fallback timeout (seconds)',
      timeoutHint: 'How long to wait for the guest before forcing the stop'
    },

    // The create split-button menu (Luca 2026-07-23): from scratch opens the
    // wizard, the other two entries are roadmap stubs.
    createFromScratch: 'From scratch',
    createFromTemplate: 'From template',
    createQuickDeploy: 'TVirt QuickDeploy',

    quickDeploy: {
      title: 'TVirt QuickDeploy',
      submit: 'Deploy',
      error: 'Deploy failed',
      compatNote: 'QuickDeploy needs a QuickDeploy-compatible template: the platform customizes the guest (network, domain, activation) on first boot.',
      template: 'Template',
      noTemplates: 'No template declares QuickDeploy compatibility yet — declare it from the Templates tab (Linux with cloud-init, Windows with cloudbase-init after sysprep)',
      osLinux: 'Linux (cloud-init)',
      osWindows: 'Windows (cloudbase-init)',
      hostname: 'Host name',
      hostnameHint: 'Host name or FQDN; empty = the VM name (Windows: 15 characters at most)',
      ip: 'IP address',
      ipHint: 'Host and prefix, e.g. 192.168.122.101/24; empty = DHCP',
      gateway: 'Gateway',
      dns: 'DNS servers',
      dnsHint: 'Up to three, comma-separated',
      count: 'Instances',
      countHint: 'More than one: named <name>-1…-N, DHCP only',
      user: 'User',
      userHint: 'Empty = the image\'s default user (Linux) or Administrator (Windows)',
      password: 'Password',
      passwordHint: 'Written into the generated user-data, which the platform then declares secret: nobody reads it back',
      sshKeys: 'SSH public keys',
      sshKeysHint: 'One per line (ssh-ed25519 AAAA… comment)',
      productKey: 'Windows product key',
      kmsServer: 'KMS server',
      kmsHint: 'Host or host:port — needs a product key (a KMS client key)',
      adDomain: 'Join Active Directory domain',
      adDomainHint: 'Empty = no domain join',
      adUser: 'Join account',
      adPassword: 'Join account password',
      adOu: 'Target OU (optional)',
      advanced: 'Advanced: raw documents',
      userData: 'user-data (raw)',
      userDataHint: 'Supersedes the generated user-data: the user, password, keys and Windows fields above must stay empty',
      networkConfig: 'network-config (raw)',
      networkConfigHint: 'Supersedes the generated network: IP, gateway and DNS above must stay empty',
      secret: 'Treat the raw user-data as secret',
      secretHint: 'Marked secret, it stops being returned by the API to anybody and travels sealed with the masterpass inside the Checkpoint',
      startNow: 'Start right away',
      startNowHint: 'Each instance starts once defined; the seed is built at that launch'
    },

    create: {
      button: 'Create VM',
      title: 'Create virtual machine',
      dialogTitle: 'Create virtual machine',
      stepGeneral: 'General',
      stepGeneralHint: 'Name and profile',
      stepCompute: 'CPU & RAM',
      stepComputeHint: 'Topology and sizing',
      stepDisks: 'Disks',
      stepDisksHint: 'Create or attach',
      stepCdroms: 'CD/DVD',
      stepCdromsHint: 'Drives and media',
      stepNetwork: 'Network',
      stepNetworkHint: 'NICs: trunk or bridge',
      stepSummary: 'Summary',
      stepSummaryHint: 'Review and create',
      name: 'Name',
      nameHint: 'Lowercase letters, digits and dashes (DNS-1123)',
      nextButton: 'Next',
      prevButton: 'Previous',
      submitButton: 'Create',
      creating: 'Creating…',
      sockets: 'Sockets',
      cores: 'Cores per socket',
      threads: 'Threads per core',
      totalVcpus: 'Total vCPU',
      memoryGib: 'Memory (GiB)',
      cpuModelHint: "'host' exposes the host CPU",
      cpuLimitHint: 'Host cores cap — empty = unlimited',
      cpuNestedHint: "Exposes vmx/svm to the guest so it can run its own hypervisor — requires the host CPU model and a node with nested enabled",
      newDisk: 'New disk',
      attachDisk: 'Attach existing disk',
      diskStorage: 'Storage',
      diskSize: 'Size (GiB)',
      diskSelect: 'Disk',
      addDisk: 'Add disk',
      addCdrom: 'Add CD/DVD drive',
      addNic: 'Add network interface',
      remove: 'Remove',
      slotLimit: 'Slot limit reached',
      cdromImage: 'Image (empty drive if none)',
      nicAttachment: 'Attachment',
      nicAttachmentTrunk: 'Overlay trunk',
      nicAttachmentBridge: 'Host bridge',
      nicAttachmentNetwork: 'Logical network (fabric)',
      nicNetwork: 'Logical network',
      nicNetworkHint:
        'The RECOMMENDED way (F3): the NIC realizes on the DERIVED bridge of the serving node — the bridge is born at the explicit fabric apply, and a start on a node not carrying it yet refuses speaking. When the last reference in the fleet leaves, the bridge falls out at the next apply.',
      nicUntagged: 'Untagged overlay',
      nicUntaggedNone: 'None (tagged only)',
      nicTrunkHint: 'At least one side: an untagged overlay and/or tagged VLAN mappings — the VM can only run on hosts every referenced overlay declares',
      nicTaggedVlan: 'VLAN ID',
      nicTaggedOverlay: 'Overlay network',
      addTagged: 'Add tagged VLAN',
      nicBridge: 'Host bridge name',
      nicBridgeHint: 'NM host bridge by name — the VM can only run on servers whose host network declares this bridge',
      nicMac: 'MAC (auto if empty)',
      noDisksHint: 'No disks: the VM will only have the CD/DVD drives you add.',
      noCdromsHint: 'No CD/DVD drives: add one to boot from an ISO image.',
      mountGuestTools: 'Mount TVirt Guest Tools at first start',
      mountGuestToolsHint: 'Puts the virtio drivers and the guest agent ISO in an empty CD drive (added if needed) — the Windows installer needs them to see its disks.',
      noNicsHint: 'No network interfaces: the VM will boot without connectivity.',
      taggedMappings: 'Tagged VLAN mappings',
      summaryDevices: 'Devices',
      bootOrderTitle: 'Boot order',
      bootOrderHint: 'Drag to reorder — the first bootable device wins; NICs boot PXE',
      bootOrderUp: 'Move up',
      bootOrderDown: 'Move down',
      createError: 'Creation failed'
    },
    edit: {
      button: 'Edit',
      title: 'Edit virtual machine',
      cancel: 'Cancel',
      save: 'Save',
      error: 'Update failed',
      modelStoppedOnly: 'Changeable only while the VM is stopped',
      firmwareStoppedOnly: 'Firmware changeable only while the VM is stopped — the next start boots the new firmware and the UEFI variables stay',
      hugePagesStoppedOnly: 'Changeable only while the VM is stopped — the next start builds the memory on the new backing',
      runningHint: 'Changes apply at the next power cycle',
      sectionCpu: 'CPU',
      sectionMemory: 'Memory',
      sectionPolicy: 'Policy'
    },
    devices: {
      title: 'Devices',
      usb: 'USB',
      gpu: 'GPU',
      addUsb: 'Add USB device',
      addUsbTitle: 'Require a USB device class',
      usbClassHint:
        'The VM asks for a CLASS of USB device (a usb-device tag value), never a specific one: at start the concrete free device is picked and claimed on the chosen node. Tag registered devices under Cluster › USB and PCIe resources.',
      usbTag: 'Tag (usb-device)',
      usbValue: 'Class value',
      noUsbTags: 'No usb-device tags yet — create one and tag the registered devices under Cluster › USB and PCIe resources.',
      noUsbValues: 'This tag has no declared values yet.',
      usbEmpty: 'No USB requirements — add a class to pass a device through at start.',
      usbClassChip: 'class',
      usbPortChip: 'port',
      usbUnclaimed: 'No device claimed — resolved when the VM starts.',
      addGpu: 'Add GPU',
      addGpuTitle: 'Require a GPU class',
      gpuClassHint:
        'The VM asks for a CLASS of GPU (a gpu-device tag value), never a specific card: at start the concrete free card is picked and claimed on the chosen node, and its WHOLE IOMMU group passes through together. Tag registered GPUs under Cluster › USB and PCIe resources.',
      gpuTag: 'Tag (gpu-device)',
      gpuValue: 'Class value',
      noGpuTags: 'No gpu-device tags yet — create one and tag the registered GPUs under Cluster › USB and PCIe resources.',
      noGpuValues: 'This tag has no declared values yet.',
      gpuEmpty: 'No GPU requirements — add a class to pass a card through at start.',
      gpuClassChip: 'class',
      gpuGroupChip: 'IOMMU group ×',
      gpuUnclaimed: 'No card claimed — resolved when the VM starts.',
      gpuPowerCycle: 'No GPU hotplug — changes apply at the next power cycle (PendingRestart)',
      actions: 'Actions',
      attachDisk: 'Attach disk',
      attachDiskTitle: 'Attach a disk',
      attach: 'Attach',
      noFreeDisks: 'No unused disks — create one under Virtual machines › Unused disks.',
      addCdrom: 'Add drive',
      addCdromTitle: 'Add a CD/DVD drive',
      editCdromTitle: 'Change CD/DVD image',
      changeMedia: 'Change CD/DVD image',
      ejectMedium: 'Eject',
      mountGuestTools: 'Mount TVirt Guest Tools',
      guestToolsMounted: 'TVirt Guest Tools mounted',
      guestToolsClickMount: 'Puts the TVirt Guest Tools ISO in the first empty CD/DVD drive',
      guestToolsClickUnmount: 'Click to eject — the drive stays, empty',
      guestToolsNoSlot: 'No empty CD/DVD drive — add a drive (or eject a medium) to mount the TVirt Guest Tools',

      // Round #142 (T142-P4): the node moved to another Guest Tools
      // version under the mounted pin — the skew note beside the medium.
      guestToolsSkew: 'Mounted v{pinned}; node {node} now serves v{current}: eject and re-mount to update',
      ejectGuestTools: 'Eject TVirt Guest Tools (the drive stays, empty)',
      guestToolsEjectConfirmTitle: 'Eject TVirt Guest Tools?',
      guestToolsEjectConfirmBody: 'The medium is ejected from the running VM — the CD/DVD drive stays, empty.',
      applyLive: 'Apply immediately (the VM is running)',
      addNic: 'Add interface',
      addNicTitle: 'Add a network interface',
      editNicTitle: 'Edit network interface',
      bridgeChip: 'bridge',
      networkChip: 'fabric network',
      cable: 'Virtual cable',
      plug: 'Plug the virtual cable',
      unplug: 'Unplug the virtual cable',
      remove: 'Remove',
      removeTitle: 'Remove {device}?',
      removeBody: 'On a running VM the change applies at the next power cycle. Disks and images are detached, never deleted.'
    }
  }
}

export const itIT: typeof enUS = {
  vms: {
    spof: {
      title: 'Indipendenza dal nodo',
      single: 'Punto singolo di guasto',
      alternativesNow: 'Alternative adesso',
      structural: 'Per costruzione',
      binding: 'Vincolo che stringe',
      movableNow: 'Migrabile adesso',
    },
    title: 'Macchine virtuali',
    migratingChip: 'Migrazione in corso',
    templateChip: 'Template',
    expendableTooltip: 'In un cluster degradato è fra le prime a cadere e potrebbe non ripartire dopo la perdita di un nodo.',
    expendableHaTooltip: 'Sempre riavviata finché il cluster è sano; in un cluster degradato le VM protette vengono prima — può essere fermata o restare spenta finché il cluster non torna intero, poi riparte da sola.',
    viewDetails: 'Vedi dettagli',
    profileLegacy: 'Altro OS',
    hugePagesNone: 'Nessuna — memoria normale',
    hugePages2M: 'Pagine da 2 MiB',
    hugePages1G: 'Pagine da 1 GiB',
    searchPlaceholder: 'Cerca per nome',
    name: 'Nome',

    // Terminologia UNA (#94a): la phase si chiama 'Fase' ovunque — la
    // vecchia chiave 'state' è morta col polish della lista.
    vcpus: 'vCPU',
    memory: 'Memoria',
    ipAddresses: 'Indirizzi IP',
    noIp: 'Nessun IP',
    authorityNode: 'Authority',
    authorityNextStart: 'assegnata al prossimo avvio',
    scope: 'Scope',
    empty: 'Nessuna macchina virtuale',
    loadError: 'Impossibile caricare le macchine virtuali — il backend TVirt è raggiungibile?',
    filterOverlay: 'rete overlay',
    filterDisk: 'disco',
    filterImage: 'immagine',
    filteredBy: 'Filtrate per {kind}',
    filterPhase: 'Fase',
    filterProtection: 'Protezione',
    filterAll: 'Tutte',
    filterScopePlaceholder: 'Filtra per scope',
    filtersToggle: 'Filtri',
    scopeTree: {
      title: 'Scope',
      all: 'Tutto il cluster',
      noChildren: 'Nessun sotto-scope qui',
      recursive: 'Includi discendenti',
      collapse: 'Comprimi il pannello scope',
      up: 'Sali di livello',
      countTooltip: '{direct} VM in questo scope · {total} con i discendenti'
    },

    // Il picker ISO a due passi (#85): prima il catalogo, poi le sue ISO.
    isoPicker: {
      catalog: 'Catalogo',
      unknownCatalog: 'Fuori dai cataloghi visibili',
      noIsos: 'Nessuna immagine ISO pronta in questo catalogo'
    },
    actionsHeader: 'Azioni',
    templatesTab: {
      quickDeploy: 'QuickDeploy',
      hardware: 'Hardware',
      tags: 'Tag',
      age: 'Età',
      disk: 'disco',
      disks: 'dischi',
      empty: 'Nessun template — segna come template una VM spenta, oppure clona una VM come template.',
      clone: 'Clona / deploy',
      editTags: 'Modifica tag',

      // Round K3 (#64): esporta il template come file .tvtpl del catalogo.
      exportAction: 'Esporta nel catalogo (.tvtpl)',
      exportTitle: 'Esporta nel catalogo',
      exportSubmit: 'Esporta',
      exportError: 'Esportazione non riuscita',
      exportHint: 'Il template viene impacchettato in un archivio .tvtpl (manifest hardware + ogni disco appiattito in qcow2) come nuovo file Template VM nel catalogo scelto. NIC, supporti CD/DVD e cloud-init non viaggiano — la spec del formato elenca ogni omissione deliberata.',
      exportName: 'Nome del file',
      exportNameHint: 'Vuoto = <template>-export-<n>',
      exportStorageDefault: 'Lo storage metadati del template',

      // Importa da catalogo (Luca 2026-08-03): l'ingresso in toolbar del tab
      // Template — il backend K3/K4 copre tutti i formati vm-template, la
      // scelta è il FILE (il formato viaggia in sola lettura).
      importAction: 'Importa da catalogo',
      importTitle: 'Importa da catalogo',
      importHint: 'Un file Template VM ready del catalogo scelto diventa un template dell\'inventario: il formato del file (tvtpl, ova, ovf o qcow2 nudo) decide da solo come si importa — tu scegli soltanto il file.',
      importFile: 'File di catalogo',
      importFileHint: 'Si importano solo file ready — uno a metà upload non è importabile',
      importNoFiles: 'Nessun file Template VM ready in questo catalogo — prima caricane o esportane uno'
    },
    templateDetail: {
      quickDeploy: 'Compatibilità QuickDeploy',
      quickDeployNone: 'Non dichiarata',
      quickDeployHint: 'Dichiarala solo se l\'immagine golden contiene cloud-init (Linux) o cloudbase-init dopo sysprep /generalize (Windows): la piattaforma si fida della dichiarazione e non guarda mai dentro il guest.',
      notFound: 'Template non trovato',
      frozen: 'La configurazione hardware è congelata: riconverti il template in VM per modificarla. Nome, note e tag restano modificabili.',
      storage: 'Storage',
      size: 'Dimensione',
      configTitle: 'Configurazione (congelata)',
      usbClasses: 'Classi USB richieste',
      gpuClasses: 'Classi GPU richieste'
    },

    // Round #173 (T173-P8): the shared-disks tab, the DECLARED-property sister
    // of unusedDisksTab, plus the sharing/LUN fields of the disk dialogs.
    sharedDisksTab: {
      title: 'Dischi condivisi',
      sharing: 'Condivisione',
      sharingNone: 'Un solo attach',
      sharingMultiwriter: 'Scrittura condivisa (multiwriter)',
      sharingReadonly: 'Sola lettura condivisa',
      usedBy: 'In uso da',
      attachedTo: 'Collegato a',
      notAttached: 'Collegato a nessuna VM',
      changeSharing: 'Cambia modo di condivisione',
      changeSharingTitle: 'Modo di condivisione',
      changeSharingLocked: 'Il modo si cambia solo quando nessuna VM usa il disco',
      sharingChanged: 'Modo di condivisione aggiornato.',
      lun: 'LUN intera',
      lunNumber: 'LUN / namespace',
      lunWwn: 'WWN',
      lunSerial: 'Seriale',
      antiAffinityWarning: 'Due o più membri girano sullo stesso nodo senza una regola di anti-affinity: il guasto di un nodo li porta giù tutti',
      antiAffinityCreate: 'Crea la regola',
      createSourceLun: 'Adotta una LUN intera del target',
      createLunNumber: 'Numero di LUN (namespace id su NVMe)',
      createLunHint: 'Non si alloca nulla: la dimensione si osserva dal device all\'adozione',
      noPr: 'In questa release non ci sono le SCSI-3 persistent reservation: un Windows Failover Cluster con disco condiviso classico non è validato; i cluster che coordinano via rete (GFS2/OCFS2, RHEL HA, database a disco condiviso) sì'
    },
    unusedDisksTab: {
      title: 'Dischi non usati',
      attach: 'Collega a una VM',
      attachTitle: 'Collega a una VM',
      vm: 'Macchina virtuale',
      noVms: 'Nessuna VM disponibile',
      noSlots: 'Nessuno slot libero su questa VM',

      // Crea disco standalone (#87): tornato dall'Inventario pensionato, ora
      // con campo scope ESPLICITO (non c'è una VM da cui ereditare).
      create: 'Crea disco',
      createTitle: 'Crea disco',

      // #263: il disco nuovo seminato da un'immagine del catalogo — la size
      // si precompila dalla dimensione VIRTUALE (mai da quella del file) e
      // può solo crescere.
      createSourceBlank: 'Disco vuoto',
      createSourceImage: 'Da un\'immagine del catalogo',
      createImage: 'Immagine',
      createImageEmpty: 'Nessuna immagine template VM pronta nei cataloghi visibili',
      createSizeFromImageHint: 'Precompilata con la dimensione virtuale dell\'immagine — può solo crescere',

      // #107 (DISTRUTTIVO, ratificato): qcow2 → catalog file vm-template
      // sullo stesso storage, zero byte copiati — il disco cessa di esistere.
      convert: 'Converti in template VM…',
      convertOnlyQcow2: 'Si convertono solo dischi qcow2 — raw non è un formato template registrabile',
      convertTitle: 'Converti in template VM',
      convertSubmit: 'Converti (distrugge il disco)',
      convertError: 'Impossibile avviare la conversione',
      convertWarning: 'DISTRUTTIVO: il disco diventa un file template VM nel catalogo scelto — stesso storage, zero byte copiati — e CESSA DI ESISTERE come disco. Non si torna indietro se non creando un nuovo disco dal template.',
      convertNameHint: 'Il nome del file di catalogo — predefinito quello del disco',
      convertConfirm: 'Ho capito: il disco verrà distrutto',
      convertStarted: 'Conversione avviata — il template compare nel catalogo al termine del task.',
      storageLockHint: 'Lo storage cambia solo con l\'azione Sposta',
      source: 'Provenienza',
      sourceClone: 'Clone di un altro disco',
      sourceCloneLinked: 'Linked clone di un altro disco',
      sourceImage: 'Creato da un\'immagine',
      sourceBlank: 'Creato vuoto',
      allocated: 'Allocato / dimensione virtuale',
      observedAt: 'Osservato il',
      snapshots: 'Snapshot',
      snapshotsEmpty: 'Nessuno snapshot'
    },
    phase: {
      defined: 'Mai accesa',
      starting: 'In avvio',
      running: 'In esecuzione',
      stopping: 'In arresto',
      stopped: 'Spenta',
      paused: 'In pausa',
      failed: 'In errore',
      deleting: 'In eliminazione'
    },
    stopCause: {
      'api': 'Arrestata da TVirt (azione o API)',
      'guest-shutdown': 'Shutdown avviato dal sistema operativo guest',
      'crash': 'Crash del guest',
      'qemu-exit': 'Il processo QEMU è terminato in modo inatteso',
      'host-reboot': 'Il nodo è stato riavviato',
      'migrated': 'Migrata su un altro nodo',
      'fenced': 'Terminata dal fencing HA (nodo rientrato)'
    },
    detail: {
      notFound: 'Macchina virtuale non trovata',
      details: 'Dettagli',
      id: 'ID',
      scope: 'Scope',
      profile: 'Profilo hardware',
      firmware: 'Firmware',
      firmwareFile: 'Versione firmware UEFI',
      firmwareUpdate: 'Aggiornamento disponibile',
      firmwareUpdateHint: "La piattaforma spedisce un firmware UEFI più recente. L'aggiornamento non è mai automatico: si applica a VM spenta, quando lo decidi tu.",
      arch: 'Architettura',
      node: 'Nodo',
      bootOrder: 'Ordine di boot',
      agent: 'Stato agent',
      agentConnected: 'Connesso',
      agentNotConnected: 'Non connesso',
      protected: 'Protetta',
      linkedClones: 'Cloni collegati',
      restartPolicy: 'Politica di riavvio',
      preferredNode: 'Nodo preferito',
      noPreference: 'Nessuna preferenza (placement automatico)',
      preferNodes: 'Tag nodo preferiti',
      requireNodes: 'Tag nodo obbligatori',
      noPlacementTags: 'Nessuno',
      placementTag: 'Tag',
      placementValue: 'Valore',
      placementAddTag: 'Aggiungi tag',
      proximityHa: 'HA di TVirt Proximity',
      proximityHaRestarts: 'riavvii HA',
      proximityHaGaveUp: 'crash loop — HA in resa',
      failoverProtection: 'Protezione in degrado',
      protectionProtected: 'Protetta',
      protectionExpendable: 'Sacrificabile in degrado',
      protectionInherit: 'Eredita',
      lastStop: 'Ultimo arresto',
      yes: 'Sì',
      no: 'No',
      tabOverview: 'Panoramica',
      tabSnapshots: 'Snapshot',
      tabBackups: 'Backup',
      hardware: 'Hardware',
      name: 'Nome',
      hwSystem: 'Sistema',
      hwCpu: 'CPU',
      hwMemory: 'Memoria',
      hwBoot: 'Avvio',
      hwIdentity: 'Identità',
      hwCluster: 'Cluster e agent',
      cpuTopology: 'Topologia CPU',
      cpuModel: 'Modello CPU',
      cpuLimit: 'Limite CPU',
      cpuWeight: 'Peso CPU',
      cpuNested: "Virtualizzazione nidificata",
      cpuNestedHint: "Espone vmx/svm al guest (solo con modello CPU host). A VM accesa la modifica vale dal prossimo power cycle (PendingRestart); il posizionamento ammette solo nodi con nested abilitato.",
      unlimited: 'Illimitato',
      memory: 'Memoria',
      balloon: 'Balloon',
      hugePages: 'Pagine enormi',
      hugePagesTradeoff: 'Meno lavoro di traduzione degli indirizzi e memoria che non finisce mai su disco; in cambio la riserva resta bloccata sul nodo finché la VM è accesa, la memoria non si deduplica più e l\'avvio fallisce se il nodo non ha abbastanza pagine libere.',
      disks: 'Dischi',
      cdroms: 'Lettori CD/DVD',
      nics: 'Interfacce di rete',
      slot: 'Slot',
      disk: 'Disco',
      encrypted: 'Cifrato',
      image: 'Immagine',
      emptyDrive: 'Lettore vuoto',
      emptyOption: '(vuoto)',
      network: 'Rete',
      mac: 'MAC',
      connected: 'Collegata',
      unplugged: 'Scollegata',
      noDevices: 'Nessuno',
      screenshot: 'Screenshot',
      capturedAt: 'Acquisito alle',
      noCapture: 'Nessuna acquisizione',
      snapshotName: 'Nome',
      snapshotCreated: 'Creato',
      snapshotMemory: 'Memoria inclusa',
      noSnapshots: 'Nessuno snapshot',
      pendingRestart: 'Modifiche in attesa: verranno applicate al prossimo power cycle.',
      applied: 'applicato',
      appliedHint: 'La VM sta girando con questo valore — quello configurato si applica al prossimo power cycle.',
      actionError: 'Azione non riuscita',
      actionStillRunning: 'L\'azione è ancora in corso: l\'esito non è ancora noto',
      tabConsole: 'Console',
      consoleConnect: 'Connetti',
      consoleDisconnect: 'Disconnetti',
      consoleConnecting: 'Connessione…',
      consoleReconnecting: 'Connessione…',
      consoleUnavailable: 'La console è disponibile con la VM in esecuzione o in pausa.',
      consoleError: 'Connessione alla console non riuscita',
      consoleTaken: 'Sessione console presa da {user}',
      consoleReconnect: 'Riconnetti',
      consoleCtrlAltDel: 'Ctrl+Alt+Canc',
      consoleHold: 'Tieni premuto {key}',
      consoleFn: 'Fn…',
      consoleFullscreen: 'Apri a schermo intero in un nuovo tab',
      consoleLoadFailed: 'VM non trovata o permessi insufficienti',
      consoleClose: 'Chiudi',
      tabStats: 'Statistiche',
      tabTasks: 'Task',
      tabLogs: 'Log',
      statsCpu: 'CPU',
      statsMemory: 'Memoria',
      statsDisk: 'I/O disco',
      statsRange: 'Intervallo',
      statsNoData: 'Nessun dato nell\'intervallo — le statistiche vengono campionate con la VM in esecuzione.',
      statsError: 'Impossibile caricare le statistiche',
      cpuUsage: 'Utilizzo',
      cpuReady: 'Ready (%RDY)',
      memUsed: 'Usata',
      memGuestAvailable: 'Disponibile (guest)',
      schema: 'Schema',
      consoleProtocol: 'Console',
      agentEnabled: 'Guest agent',
      metadataStorage: 'Storage metadati',
      startupOnBoot: 'Avvio al boot',
      startupOrder: 'Ordine di avvio',
      startupDelay: 'Ritardo di avvio',
      seconds: 's',
      enabled: 'Abilitato',
      disabled: 'Disabilitato'
    },
    actionsAvailable: 'Azioni disponibili',
    actions: {
      start: 'Avvia',
      stop: 'Arresta',
      pause: 'Pausa',
      resume: 'Riprendi',
      reset: 'Reset',
      migrate: 'Migra',
      clone: 'Clona',
      moveStorage: 'Sposta su storage…',
      moveScope: 'Sposta in scope…',
      openConsole: 'Apri console',
      forceStop: 'Forza spegnimento',
      markTemplate: 'Segna come template',
      unmarkTemplate: 'Riconverti in VM',
      error: 'Impossibile eseguire l\u2019azione'
    },
    cordon: {
      confirmTitle: 'Il nodo è in manutenzione',
      confirmHint:
        'Il cordone non si può abbassare mentre lo store del cluster è giù. Confermando dichiari di saperlo: la VM si accende lì comunque, e il consenso resta registrato sul task.',
      confirm: 'Accendi comunque'
    },
    markTemplate: {
      confirmTitle: 'Segnare {name} come template?',
      confirmBody: 'La configurazione si congela e la VM non potrà più accendersi — solo clonarsi, o tornare VM. Nome, note e tag restano modificabili.',
      confirm: 'Segna come template',
      moved: '{name} ora è un template — si è spostata nel tab Template.'
    },
    unmarkTemplate: {
      confirmTitle: 'Riconvertire {name} in VM?',
      confirmBody: 'Il template torna una VM ordinaria: modificabile e avviabile (il prossimo avvio la piazza su un nodo).',
      confirm: 'Riconverti in VM',
      moved: '{name} è di nuovo una macchina virtuale — si è spostata nel tab Macchine virtuali.'
    },

    // Round #123: riferimento a nodo per TAG (selettore #106) nei dialog
    // di start e migrazione — anteprima dell'insieme risolto inclusa.
    nodeByTag: {
      label: 'Per tag di nodo',
      hint: 'tag=valore, separati da virgola (in AND): il decider sceglie tra i nodi corrispondenti',
      invalid: 'Formato non valido — usa tag=valore, separati da virgola',
      resolved: 'Membri corrispondenti: {nodes}'
    },
    migrate: {
      title: 'Live migration',
      submit: 'Migra',
      target: 'Nodo di destinazione',
      auto: 'Automatico (Proximity)',
      hint: 'La VM si sposta senza power cycle. Ogni storage che tocca deve essere condiviso; una VM in pausa migra in pausa.',
      error: 'La migrazione è stata rifiutata'
    },
    moveStorage: {
      title: 'Sposta su storage',
      submit: 'Sposta',
      target: 'Storage di destinazione',
      noTargets: 'Nessuno storage accetta dischi',
      hint: 'Tutti i dischi della VM spenta viaggiano sullo storage scelto, uno alla volta, conservando il loro id — quelli già a destinazione vengono saltati. Il progresso vive nella pagina task.',
      error: 'Lo spostamento è stato rifiutato'
    },
    moveScope: {
      title: 'Sposta in scope',
      submit: 'Sposta',
      target: 'Scope di destinazione',
      hint: 'La VM cambia scope immediatamente — id e dischi restano, i dischi owned la seguono, e può restare accesa. Oggi vive in {scope}. Chi la vede cambia con lo scope.',
      error: 'Lo spostamento di scope è stato rifiutato'
    },
    start: {
      title: 'Avvia VM',
      submit: 'Avvia',
      target: 'Nodo di destinazione',
      auto: 'Automatico (consigliato)',
      recommended: 'consigliato',
      hint: 'Scegli un nodo di destinazione oppure lascia decidere a Proximity tra i candidati validi. I nodi che non possono ospitarla sono disabilitati con il motivo.',
      error: 'Impossibile avviare la VM'
    },
    console: {
      media: 'Lettori CD/DVD',
      mediaGuestToolsHint: 'Contiene le TVirt Guest Tools — da qui puoi espellerle o rimuovere il lettore',
      slotOpsDeferred: 'Su questo profilo aggiunta e rimozione lettori si applicano al prossimo power cycle (i cambi di supporto sono immediati).',
      mountGuestTools: 'Monta TVirt Guest Tools',
      ejectGuestTools: 'Espelli TVirt Guest Tools',
      bootOrder: 'Ordine di boot',
      bootOrderHint: 'Il nuovo ordine si salva subito e si applica al prossimo power cycle — la sessione console resta collegata.'
    },
    snapshots: {
      create: 'Crea snapshot',
      createTitle: 'Crea uno snapshot',
      createSubmit: 'Crea',
      name: 'Nome',
      nameHint: 'Lettere, cifre, punto, trattino, underscore.',
      consistencyHint: 'La consistenza è automatica: il guest agent congela i filesystem quando risponde; altrimenti la VM va in pausa per un attimo attorno allo snapshot.',
      memory: 'Includi la memoria',
      memoryHint: 'Cattura la RAM viva: il ripristino riprende la VM in esecuzione. Richiede tutti i dischi qcow2.',
      currentChip: 'Sei qui',
      revert: 'Ripristina',
      revertTitle: 'Ripristinare {name}?',
      revertBody: 'I dischi tornano allo stato dello snapshot; tutto ciò che è successo dopo va perso. Uno snapshot solo-dischi richiede la VM ferma.',
      delete: 'Elimina',
      deleteTitle: 'Eliminare lo snapshot {name}?',
      deleteBody: 'Il punto di ripristino sparisce; il contenuto dei dischi resta intatto.',
      deleteMergeHint: 'Il suo delta si fonde in {children}: ogni punto di ripristino successivo resta valido.',
      deleteDuplication: 'Accetto la duplicazione',
      deleteDuplicationHint: 'Il delta viene copiato in ognuno dei {count} lettori (gli snapshot figli e, se questo è il corrente, la testa attiva): lo spazio occupato può crescere invece di calare.',
      error: 'L’operazione sullo snapshot è stata rifiutata'
    },
    clone: {
      title: 'Clona VM',
      submit: 'Clona',
      name: 'Nome della copia',
      storage: 'Storage di destinazione dei dischi',
      sameStorage: 'Stesso della sorgente',
      full: 'Copia completa',
      fullHint: 'Copia ogni byte e recide il legame con i dischi sorgente. Il default è un linked clone: istantaneo, ma incatenato alla sorgente (solo qcow2).',
      hint: 'Il clone mantiene configurazione e firmware della sorgente; indirizzi MAC e identità TPM sono nuovi.',
      markAs: 'Tipo del clone',
      markAsInheritVm: 'Come la sorgente (VM)',
      markAsInheritTemplate: 'Come la sorgente (template)',
      markAsVm: 'Macchina virtuale',
      markAsTemplate: 'Template',
      markAsHintVm: 'Scegli Template per promuovere la copia in un solo passo.',
      markAsHintTemplate: 'Scegli Macchina virtuale per fare il deploy da questo template in un solo passo.',
      error: 'La clonazione è stata rifiutata'
    },
    bulk: {
      selected: 'selezionate',
      deleteTitle: {
        one: 'Eliminare {count} VM selezionata?',
        many: 'Eliminare {count} VM selezionate?',
        other: 'Eliminare {count} VM selezionate?'
      },
      deleteBody: 'Ogni VM passa le stesse guardie del delete singolo; i rifiuti sono riportati per elemento.',
      done: 'accettate',
      refused: 'rifiutate'
    },
    delete: {
      button: 'Elimina',
      confirmTitle: 'Eliminare {name}?',
      confirmBody: "La macchina virtuale e i dischi di sua proprietà verranno eliminati. L'operazione non è reversibile.",
      keepDisks: 'Mantieni i dischi',
      keepDisksHint: 'I dischi creati con la VM restano nel loro storage come dischi autonomi.',
      confirm: 'Elimina',
      cancel: 'Annulla',
      error: 'Eliminazione non riuscita',
      blockedPhase: 'Arresta prima la VM',
      blockedProtected: 'La VM è protetta — rimuovi prima la protezione',
      blockedDeleting: 'Eliminazione già in corso'
    },
    logs: {
      title: 'Log',
      subtitle: 'Frame di log strutturati dei task che toccano questa VM',
      when: 'Quando',
      task: 'Task',
      level: 'Livello',
      message: 'Messaggio',
      empty: 'Ancora nessun log',
      loadError: 'Impossibile caricare i log — il backend TVirt è raggiungibile?'
    },
    stop: {
      title: 'Arresta macchina virtuale',
      modeGraceful: 'Spegnimento graceful',
      modeGracefulHint: 'Viene chiesto al sistema operativo guest di spegnersi in modo naturale (ACPI); se ignora la richiesta, dopo il timeout scatta lo spegnimento forzato.',
      modeForced: 'Spegnimento forzato',
      modeForcedHint: 'Stacca la corrente immediatamente, come togliere la spina.',
      timeout: 'Timeout di fallback (secondi)',
      timeoutHint: 'Attesa concessa al guest prima di forzare lo spegnimento'
    },

    // Il menu del bottone split di creazione (Luca 2026-07-23): "Da zero" apre
    // il wizard, le altre due voci sono stub di roadmap.
    createFromScratch: 'Da zero',
    createFromTemplate: 'Da template',
    createQuickDeploy: 'TVirt QuickDeploy',

    quickDeploy: {
      title: 'TVirt QuickDeploy',
      submit: 'Esegui deploy',
      error: 'Deploy non riuscito',
      compatNote: 'QuickDeploy richiede un template compatibile QuickDeploy: la piattaforma personalizza il guest (rete, dominio, attivazione) al primo avvio.',
      template: 'Template',
      noTemplates: 'Nessun template dichiara la compatibilità QuickDeploy — dichiarala dal tab Template (Linux con cloud-init, Windows con cloudbase-init dopo sysprep)',
      osLinux: 'Linux (cloud-init)',
      osWindows: 'Windows (cloudbase-init)',
      hostname: 'Nome host',
      hostnameHint: 'Nome host o FQDN; vuoto = il nome della VM (Windows: al massimo 15 caratteri)',
      ip: 'Indirizzo IP',
      ipHint: 'Host e prefisso, es. 192.168.122.101/24; vuoto = DHCP',
      gateway: 'Gateway',
      dns: 'Server DNS',
      dnsHint: 'Fino a tre, separati da virgola',
      count: 'Istanze',
      countHint: 'Più di una: si chiamano <nome>-1…-N, solo DHCP',
      user: 'Utente',
      userHint: 'Vuoto = l\'utente predefinito dell\'immagine (Linux) o Administrator (Windows)',
      password: 'Password',
      passwordHint: 'Scritta nella user-data generata, che la piattaforma dichiara poi segreta: nessuno la rilegge',
      sshKeys: 'Chiavi pubbliche SSH',
      sshKeysHint: 'Una per riga (ssh-ed25519 AAAA… commento)',
      productKey: 'Product key Windows',
      kmsServer: 'Server KMS',
      kmsHint: 'Host o host:porta — richiede un product key (una chiave client KMS)',
      adDomain: 'Join dominio Active Directory',
      adDomainHint: 'Vuoto = nessun join di dominio',
      adUser: 'Account di join',
      adPassword: 'Password dell\'account di join',
      adOu: 'OU di destinazione (opzionale)',
      advanced: 'Avanzate: documenti grezzi',
      userData: 'user-data (grezza)',
      userDataHint: 'Scavalca la user-data generata: utente, password, chiavi e campi Windows qui sopra devono restare vuoti',
      networkConfig: 'network-config (grezza)',
      networkConfigHint: 'Scavalca la rete generata: IP, gateway e DNS qui sopra devono restare vuoti',
      secret: 'Tratta la user-data grezza come segreta',
      secretHint: 'Marcata segreta, smette di essere restituita dall\'API a chiunque e nel Checkpoint viaggia sigillata con la masterpass',
      startNow: 'Avvia subito',
      startNowHint: 'Ogni istanza parte appena definita; il seed nasce a quel lancio'
    },

    create: {
      button: 'Crea VM',
      title: 'Crea macchina virtuale',
      dialogTitle: 'Crea macchina virtuale',
      stepGeneral: 'Generale',
      stepGeneralHint: 'Nome e profilo',
      stepCompute: 'CPU e RAM',
      stepComputeHint: 'Topologia e dimensioni',
      stepDisks: 'Dischi',
      stepDisksHint: 'Crea o collega',
      stepCdroms: 'CD/DVD',
      stepCdromsHint: 'Lettori e supporti',
      stepNetwork: 'Rete',
      stepNetworkHint: 'NIC: trunk o bridge',
      stepSummary: 'Riepilogo',
      stepSummaryHint: 'Verifica e crea',
      name: 'Nome',
      nameHint: 'Minuscole, cifre e trattini (DNS-1123)',
      nextButton: 'Avanti',
      prevButton: 'Indietro',
      submitButton: 'Crea',
      creating: 'Creazione…',
      sockets: 'Socket',
      cores: 'Core per socket',
      threads: 'Thread per core',
      totalVcpus: 'vCPU totali',
      memoryGib: 'Memoria (GiB)',
      cpuModelHint: "'host' espone la CPU dell'host",
      cpuLimitHint: 'Tetto in core host — vuoto = illimitato',
      cpuNestedHint: "Espone vmx/svm al guest perché possa far girare un proprio hypervisor — richiede il modello CPU host e un nodo con nested abilitato",
      newDisk: 'Nuovo disco',
      attachDisk: 'Collega disco esistente',
      diskStorage: 'Storage',
      diskSize: 'Dimensione (GiB)',
      diskSelect: 'Disco',
      addDisk: 'Aggiungi disco',
      addCdrom: 'Aggiungi lettore CD/DVD',
      addNic: 'Aggiungi interfaccia di rete',
      remove: 'Rimuovi',
      slotLimit: 'Limite slot raggiunto',
      cdromImage: 'Immagine (lettore vuoto se assente)',
      nicAttachment: 'Collegamento',
      nicAttachmentTrunk: 'Trunk overlay',
      nicAttachmentBridge: 'Bridge host',
      nicAttachmentNetwork: 'Rete logica (fabric)',
      nicNetwork: 'Rete logica',
      nicNetworkHint:
        "La via RACCOMANDATA (F3): la vNIC si realizza sul bridge DERIVATO del nodo servente — il bridge nasce all'apply esplicito della fabric, e un avvio su un nodo che non lo porta ancora rifiuta parlando. Quando l'ultima referenza della flotta se ne va, il bridge esce al prossimo apply.",
      nicUntagged: 'Overlay untagged',
      nicUntaggedNone: 'Nessuna (solo tagged)',
      nicTrunkHint: 'Almeno un lato: una overlay untagged e/o mappature VLAN tagged — la VM può girare solo sugli host dichiarati da ogni overlay referenziata',
      nicTaggedVlan: 'ID VLAN',
      nicTaggedOverlay: 'Rete overlay',
      addTagged: 'Aggiungi VLAN tagged',
      nicBridge: 'Nome del bridge host',
      nicBridgeHint: 'Bridge NM per nome — la VM può girare solo sui server il cui host network dichiara questo bridge',
      nicMac: 'MAC (auto se vuoto)',
      noDisksHint: 'Nessun disco: la VM avrà solo i lettori CD/DVD che aggiungi.',
      noCdromsHint: 'Nessun lettore CD/DVD: aggiungine uno per avviare da immagine ISO.',
      mountGuestTools: 'Monta TVirt Guest Tools al primo avvio',
      mountGuestToolsHint: "Mette la ISO con i driver virtio e il guest agent in un lettore CD vuoto (aggiunto se serve) — l'installer di Windows ne ha bisogno per vedere i dischi.",
      noNicsHint: 'Nessuna interfaccia di rete: la VM si avvierà senza connettività.',
      taggedMappings: 'Mapping VLAN tagged',
      summaryDevices: 'Dispositivi',
      bootOrderTitle: 'Ordine di boot',
      bootOrderHint: 'Trascina per riordinare — vince il primo dispositivo avviabile; le NIC avviano PXE',
      bootOrderUp: 'Sposta su',
      bootOrderDown: 'Sposta giù',
      createError: 'Creazione non riuscita'
    },
    edit: {
      button: 'Modifica',
      title: 'Modifica macchina virtuale',
      cancel: 'Annulla',
      save: 'Salva',
      error: 'Modifica non riuscita',
      modelStoppedOnly: 'Modificabile solo a VM spenta',
      firmwareStoppedOnly: 'Firmware modificabile solo a VM spenta — al prossimo avvio la macchina parte col firmware nuovo e le variabili UEFI restano',
      hugePagesStoppedOnly: 'Modificabile solo a VM spenta — al prossimo avvio la memoria nasce col nuovo backing',
      runningHint: 'Le modifiche si applicano al prossimo power cycle',
      sectionCpu: 'CPU',
      sectionMemory: 'Memoria',
      sectionPolicy: 'Criteri'
    },
    devices: {
      title: 'Dispositivi',
      usb: 'USB',
      gpu: 'GPU',
      addUsb: 'Aggiungi dispositivo USB',
      addUsbTitle: 'Richiedi una classe di dispositivi USB',
      usbClassHint:
        'La VM chiede una CLASSE di dispositivi USB (un valore di un tag usb-device), mai un dispositivo puntuale: alla partenza viene scelto e riservato il dispositivo libero concreto sul nodo. Tagga i dispositivi registrati in Cluster › Risorse USB e PCIe.',
      usbTag: 'Tag (usb-device)',
      usbValue: 'Valore della classe',
      noUsbTags: 'Nessun tag usb-device — creane uno e tagga i dispositivi registrati in Cluster › Risorse USB e PCIe.',
      noUsbValues: 'Questo tag non ha ancora valori dichiarati.',
      usbEmpty: 'Nessun requisito USB — aggiungi una classe per passare un dispositivo alla partenza.',
      usbClassChip: 'classe',
      usbPortChip: 'porta',
      usbUnclaimed: 'Nessun dispositivo riservato — si risolve alla partenza della VM.',
      addGpu: 'Aggiungi GPU',
      addGpuTitle: 'Richiedi una classe di GPU',
      gpuClassHint:
        'La VM chiede una CLASSE di GPU (un valore di un tag gpu-device), mai una scheda puntuale: alla partenza la scheda libera concreta viene scelta e riservata sul nodo, e passa INSIEME tutto il suo gruppo IOMMU. Tagga le GPU registrate in Cluster › Risorse USB e PCIe.',
      gpuTag: 'Tag (gpu-device)',
      gpuValue: 'Valore della classe',
      noGpuTags: 'Nessun tag gpu-device — creane uno e tagga le GPU registrate in Cluster › Risorse USB e PCIe.',
      noGpuValues: 'Questo tag non ha ancora valori dichiarati.',
      gpuEmpty: 'Nessun requisito GPU — aggiungi una classe per passare una scheda alla partenza.',
      gpuClassChip: 'classe',
      gpuGroupChip: 'gruppo IOMMU ×',
      gpuUnclaimed: 'Nessuna scheda riservata — si risolve alla partenza della VM.',
      gpuPowerCycle: 'Niente hotplug GPU — le modifiche valgono dal prossimo power cycle (PendingRestart)',
      actions: 'Azioni',
      attachDisk: 'Aggiungi disco',
      attachDiskTitle: 'Aggiungi un disco',
      attach: 'Collega',
      noFreeDisks: 'Nessun disco non usato — creane uno in Macchine virtuali › Dischi non usati.',
      addCdrom: 'Aggiungi lettore',
      addCdromTitle: 'Aggiungi un lettore CD/DVD',
      editCdromTitle: 'Cambia immagine CD/DVD',
      changeMedia: 'Cambia immagine CD/DVD',
      ejectMedium: 'Espelli',
      mountGuestTools: 'Monta TVirt Guest Tools',
      guestToolsMounted: 'TVirt Guest Tools montate',
      guestToolsClickMount: 'Mette la ISO TVirt Guest Tools nel primo lettore CD/DVD vuoto',
      guestToolsClickUnmount: 'Clicca per espellere — il lettore resta, vuoto',
      guestToolsNoSlot: 'Nessun lettore CD/DVD vuoto — aggiungi un lettore (o espelli un supporto) per montare le TVirt Guest Tools',

      // Round #142 (T142-P4): the node moved to another Guest Tools
      // version under the mounted pin — the skew note beside the medium.
      guestToolsSkew: 'Montata la v{pinned}; il nodo {node} ora serve la v{current}: espelli e rimonta per aggiornare',
      ejectGuestTools: 'Espelli TVirt Guest Tools (il lettore resta, vuoto)',
      guestToolsEjectConfirmTitle: 'Espellere le TVirt Guest Tools?',
      guestToolsEjectConfirmBody: 'Il supporto viene espulso dalla VM accesa — il lettore CD/DVD resta, vuoto.',
      applyLive: 'Applica subito (la VM è accesa)',
      addNic: 'Aggiungi interfaccia',
      addNicTitle: 'Aggiungi un\'interfaccia di rete',
      editNicTitle: 'Modifica interfaccia di rete',
      bridgeChip: 'bridge',
      networkChip: 'rete fabric',
      cable: 'Cavo virtuale',
      plug: 'Collega il cavo virtuale',
      unplug: 'Scollega il cavo virtuale',
      remove: 'Rimuovi',
      removeTitle: 'Rimuovere {device}?',
      removeBody: 'A VM accesa la modifica si applica al prossimo power cycle. Dischi e immagini vengono scollegati, mai eliminati.'
    }
  }
}
